import {
  IonButton,
  IonContent,
  IonInput,
  IonItem,
  IonList,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useEffect, useMemo, useState } from 'react';
import './Default.css';
import AppPageHeader from '../components/AppPageHeader';
import { returnToMain } from '../utils/nav';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import {
  getAppError,
  getAppSuccess,
  getUserMessageOrValidationError,
} from '../utils/messages';
import useEnhancedAuth from '../EnhancedAuth';
import { storageGetCards } from '../utils/storage';
import { Farecard } from '../services/types/apitypes';
import { cardsLoadAndSelect } from '../utils/cards';
import { apiGetUser, apiHelpRequest } from '../services/api';
import { LoadingController } from '../utils/LoadingController';
import { useToaster } from '../hooks/useToaster';
import { formatCreditCard } from '../utils/strings';
import { uiInputMode } from '../utils/ui';

const MAX_ATTACHMENTS = 2;
const MAX_ATTACHMENT_SIZE = 2 * 1024 * 1024;
const ALLOWED_ATTACHMENT_TYPES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
];

const CASE_REASON_OPTIONS = [
  'Card Lost / Stolen',
  'Refund Request',
  'Card Not Received',
  'Card Not Reading',
  'Equipment Issue',
  'General Inquiry',
  'Card Order',
  'Discount Fare Application',
  'Balance Issue',
  'Fare Inspection',
  'Account Document Request',
  'Mobile Card Issue',
  'Open Payments',
  'Other',
  'Ride Sponsor Account Inquiry',
  'Retail Outlet Issue',
  'Rewards Program',
];

const CASE_CATEGORY_OPTIONS = ['Support Request', 'Question', 'Feedback'];

const FeedbackPage: React.FC = () => {
  const auth = useEnhancedAuth();
  const [t] = useTranslation('global');
  const router = useHistory();
  const [present] = useToaster();

  const [cards, setCards] = useState<Farecard[] | undefined>(storageGetCards());
  const [customerId, setCustomerId] = useState('');
  const [customerLoaded, setCustomerLoaded] = useState(false);
  const [cardsLoaded, setCardsLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [caseReason, setCaseReason] = useState('');
  const [caseCategory, setCaseCategory] = useState('');
  const [card, setCard] = useState('');
  const [attachmentOne, setAttachmentOne] = useState<File | null>(null);
  const [attachmentTwo, setAttachmentTwo] = useState<File | null>(null);

  const attachmentInputOneId = 'feedback-attachments-input-1';
  const attachmentInputTwoId = 'feedback-attachments-input-2';

  useEffect(() => {
    apiGetUser(auth)
      .then((result) => {
        setCustomerId(result.data.id || '');
      })
      .catch((error) => {
        console.error('Error fetching user data:', error);
      })
      .finally(() => {
        setCustomerLoaded(true);
      });
  }, [auth]);

  useEffect(() => {
    if (!cards || cards.length === 0) {
      cardsLoadAndSelect(
        '',
        (loadedCards) => {
          if (loadedCards?.length) {
            setCards(loadedCards);
          }
          setCardsLoaded(true);
        },
        () => null,
        auth,
      ).catch((error) => {
        console.error('Error loading cards:', error);
        setCardsLoaded(true);
      });
    } else {
      setCardsLoaded(true);
    }
  }, [auth, cards]);

  useEffect(() => {
    if (!customerLoaded || !cardsLoaded) {
      LoadingController.show();
      return;
    }

    LoadingController.hide();
  }, [cardsLoaded, customerLoaded]);

  const cardOptions = useMemo(
    () =>
      (cards || []).map((farecard) => ({
        cardId: farecard.cardId,
        label: farecard.nickname
          ? `${farecard.nickname} (${formatCreditCard(farecard.cardId)})`
          : formatCreditCard(farecard.cardId),
      })),
    [cards],
  );

  const [isPageActive, setIsPageActive] = useState(false);

  const attachments = useMemo(
    () => [attachmentOne, attachmentTwo].filter((attachment): attachment is File => Boolean(attachment)),
    [attachmentOne, attachmentTwo],
  );

  useIonViewDidEnter(() => {
    setIsPageActive(true);
    document
      .querySelector('meta[name="viewport"]')
      ?.setAttribute(
        'content',
        'viewport-fit=cover,width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=3,user-scalable=yes',
      );
  });

  useIonViewWillLeave(() => {
    setIsPageActive(false);
    document
      .querySelector('meta[name="viewport"]')
      ?.setAttribute(
        'content',
        'viewport-fit=cover,width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1,user-scalable=no',
      );
  });

  const validateAttachments = (files: File[]) => {
    if (files.length > MAX_ATTACHMENTS) {
      return String(t('feedback.errorAttachmentCount', { n: MAX_ATTACHMENTS }));
    }

    if (files.some((attachment) => !ALLOWED_ATTACHMENT_TYPES.includes(attachment.type))) {
      return String(t('feedback.errorAttachmentType'));
    }

    if (files.some((attachment) => attachment.size > MAX_ATTACHMENT_SIZE)) {
      return String(t('feedback.errorAttachmentSize', { size: 2 }));
    }

    return null;
  };

  const handleAttachmentChange = (
    slot: 1 | 2,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFile = event.target.files?.[0] || null;
    const nextAttachmentOne = slot === 1 ? selectedFile : attachmentOne;
    const nextAttachmentTwo = slot === 2 ? selectedFile : attachmentTwo;
    const nextAttachments = [nextAttachmentOne, nextAttachmentTwo].filter(
      (attachment): attachment is File => Boolean(attachment),
    );
    const errorMessage = validateAttachments(nextAttachments);

    if (errorMessage) {
      present(getAppError(errorMessage));
      event.target.value = '';
      return;
    }

    if (slot === 1) {
      setAttachmentOne(selectedFile);
    } else {
      setAttachmentTwo(selectedFile);
    }

    event.target.value = '';
  };

  const handleAttachmentRemove = (slot: 1 | 2) => {
    if (slot === 1) {
      if (attachmentTwo) {
        setAttachmentOne(attachmentTwo);
        setAttachmentTwo(null);
        return;
      }

      setAttachmentOne(null);
      return;
    }

    setAttachmentTwo(null);
  };

  const handleSubmit = async () => {
    if (!contactName.trim()) {
      present(getAppError(String(t('feedback.errorname'))));
      return;
    }

    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email.trim())) {
      present(getAppError(String(t('feedback.erroremail'))));
      return;
    }

    if (!phone.trim() || !/^[0-9\-+()\s]{5,}$/.test(phone.trim())) {
      present(getAppError(String(t('feedback.errorphone'))));
      return;
    }

    if (!subject.trim()) {
      present(getAppError(String(t('feedback.errorsubject'))));
      return;
    }

    if (subject.trim().length > 80) {
      present(getAppError(String(t('feedback.errorsubjectmax', { n: 80 }))));
      return;
    }

    if (!description.trim()) {
      present(getAppError(String(t('feedback.errorbody'))));
      return;
    }

    if (!caseReason) {
      present(getAppError(String(t('feedback.errorreason'))));
      return;
    }

    if (!caseCategory) {
      present(getAppError(String(t('feedback.errorCategory'))));
      return;
    }

    const attachmentError = validateAttachments(attachments);
    if (attachmentError) {
      present(getAppError(attachmentError));
      return;
    }

    const formData = new FormData();
    formData.append('contactName', contactName.trim());
    formData.append('email', email.trim());
    formData.append('phoneNumber', phone.trim());
    formData.append('subject', subject.trim());
    formData.append('description', description.trim());
    formData.append('caseReason', caseReason);
    formData.append('caseCategory', caseCategory);
    if (card) {
      formData.append('card', card);
    }
    formData.append('deviceType', navigator.userAgent);
    attachments.forEach((attachment) => formData.append('attachments', attachment));

    try {
      LoadingController.show();
      setIsSubmitting(true);
      await apiHelpRequest(formData, auth);
      present(getAppSuccess(String(t('feedback.success'))));
      setTimeout(() => {
        LoadingController.hide();
        returnToMain(router);
      }, 1500);
    } catch (error) {
      LoadingController.hide();
      present(
        getAppError(
          String(getUserMessageOrValidationError(error) || t('feedback.nosuccess')),
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <IonPage id="account-page">
      <AppPageHeader
        isPageActive={isPageActive}
        back={() => returnToMain(router)}
        title={String(t('feedback.title'))}
      ></AppPageHeader>
      <IonContent fullscreen className="ion-padding default-content" scrollY>
        <p className="instructions">{t('feedback.subtitle')}</p>
        <p className="pt-2 instructions">{t('feedback.info')}</p>

        <IonList inset={false} className="ion-no-padding">
          <h1 className="h1 pt-2 pb-1">{t('feedback.contactName')}</h1>
          <IonItem lines="none" className="ion-no-padding pb-1">
            <IonInput
              value={contactName}
              mode={uiInputMode()}
              aria-label={String(t('feedback.contactName'))}
              placeholder={String(t('feedback.contactName'))}
              onIonInput={(e) => setContactName(e.detail.value || '')}
              type="text"
              fill="outline"
            ></IonInput>
          </IonItem>

          <h1 className="h1 pt-2 pb-1">{t('feedback.email')}</h1>
          <IonItem lines="none" className="ion-no-padding pb-1">
            <IonInput
              value={email}
              mode={uiInputMode()}
              aria-label={String(t('feedback.email'))}
              placeholder={String(t('feedback.email'))}
              onIonInput={(e) => setEmail(e.detail.value || '')}
              type="email"
              fill="outline"
            ></IonInput>
          </IonItem>

          <h1 className="h1 pt-2 pb-1">{t('feedback.phone')}</h1>
          <IonItem lines="none" className="ion-no-padding pb-1">
            <IonInput
              value={phone}
              mode={uiInputMode()}
              aria-label={String(t('feedback.phone'))}
              placeholder={String(t('feedback.phone'))}
              onIonInput={(e) => setPhone(e.detail.value || '')}
              type="tel"
              fill="outline"
            ></IonInput>
          </IonItem>

          <h1 className="h1 pt-2 pb-1">{t('feedback.subject')}</h1>
          <IonItem lines="none" className="ion-no-padding pb-1">
            <IonInput
              value={subject}
              mode={uiInputMode()}
              aria-label={String(t('feedback.subject'))}
              placeholder={String(t('feedback.subject'))}
              onIonInput={(e) => setSubject(e.detail.value || '')}
              type="text"
              maxlength={80}
              helperText={String(
                t('feedback.formhelper', {
                  max: 80,
                  current: subject.length,
                }),
              )}
              fill="outline"
            ></IonInput>
          </IonItem>

          <h1 className="h1 pt-2 pb-1">{t('feedback.body')}</h1>
          <IonItem lines="none" className="ion-no-padding pb-1">
            <IonTextarea
              value={description}
              mode={uiInputMode()}
              aria-label={String(t('feedback.body'))}
              placeholder={String(t('feedback.body'))}
              onIonInput={(e) => setDescription(e.detail.value || '')}
              rows={7}
              fill="outline"
            ></IonTextarea>
          </IonItem>

          <h1 className="h1 pt-2 pb-1">{t('feedback.caseReason')}</h1>
          <IonItem lines="none" className="ion-no-padding pb-1 pseudo-select pseudo-select-active">
            <IonSelect
              value={caseReason}
              mode={uiInputMode()}
              aria-label={String(t('feedback.caseReason'))}
              placeholder={String(t('feedback.careReasonOptions.none'))}
              onIonChange={(e) => setCaseReason(e.detail.value || '')}
            >
              {CASE_REASON_OPTIONS.map((option) => (
                <IonSelectOption key={option} value={option}>
                  {option}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

          <h1 className="h1 pt-2 pb-1">{t('feedback.customerAccountId')}</h1>
          <IonItem lines="none" className="ion-no-padding pb-1">
            <IonInput
              className="feedback-readonly-input"
              value={customerId}
              mode={uiInputMode()}
              aria-label={String(t('feedback.customerAccountId'))}
              readonly
              fill="outline"
            ></IonInput>
          </IonItem>

          <h1 className="h1 pt-2 pb-1">{t('feedback.caseCategory')}</h1>
          <IonItem lines="none" className="ion-no-padding pb-1 pseudo-select pseudo-select-active">
            <IonSelect
              value={caseCategory}
              mode={uiInputMode()}
              aria-label={String(t('feedback.caseCategory'))}
              placeholder={String(t('feedback.caseCategoryOptions.none'))}
              onIonChange={(e) => setCaseCategory(e.detail.value || '')}
            >
              {CASE_CATEGORY_OPTIONS.map((option) => (
                <IonSelectOption key={option} value={option}>
                  {option}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

          <h1 className="h1 pt-2 pb-1">{t('feedback.cardNumber')}</h1>
          <IonItem lines="none" className="ion-no-padding pb-1 pseudo-select pseudo-select-active">
            <IonSelect
              value={card}
              mode={uiInputMode()}
              aria-label={String(t('feedback.cardNumber'))}
              placeholder={String(t('feedback.careReasonOptions.none'))}
              onIonChange={(e) => setCard(e.detail.value || '')}
            >
              {cardOptions.map((option) => (
                <IonSelectOption key={option.cardId} value={option.cardId}>
                  {option.label}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

          <IonItem lines="none" className="ion-no-padding pb-3">
            <div className="feedback-attachments" style={{ width: '100%' }}>
              <h1 className="h1 pt-2 pb-1">{t('feedback.attachments')}</h1>
              <input
                id={attachmentInputOneId}
                className="feedback-attachments-input"
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={(event) => handleAttachmentChange(1, event)}
              />
              {attachmentOne ? (
                <div className="pt-1 feedback-attachments-file-row feedback-attachments-file-row-actions">
                  <IonButton
                    className="btn-default btn-inverted feedback-attachments-button feedback-attachments-inline-button"
                    onClick={() => document.getElementById(attachmentInputOneId)?.click()}
                  >
                    Replace
                  </IonButton>
                  <p className="instructions feedback-attachments-file-name">
                    {attachmentOne.name}
                  </p>
                  <IonButton
                    fill="clear"
                    size="small"
                    className="feedback-attachments-remove"
                    onClick={() => handleAttachmentRemove(1)}
                  >
                    Remove
                  </IonButton>
                </div>
              ) : (
                <IonButton
                  className="btn-default btn-inverted feedback-attachments-button"
                  onClick={() => document.getElementById(attachmentInputOneId)?.click()}
                >
                  {t('feedback.browseAttachments')}
                </IonButton>
              )}

              {attachmentOne ? (
                <>
                  <input
                    id={attachmentInputTwoId}
                    className="feedback-attachments-input"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(event) => handleAttachmentChange(2, event)}
                  />
                  {attachmentTwo ? (
                    <div className="pt-1 feedback-attachments-file-row feedback-attachments-file-row-actions">
                      <IonButton
                        className="btn-default btn-inverted feedback-attachments-button feedback-attachments-inline-button"
                        onClick={() => document.getElementById(attachmentInputTwoId)?.click()}
                      >
                        Replace
                      </IonButton>
                      <p className="instructions feedback-attachments-file-name">
                        {attachmentTwo.name}
                      </p>
                      <IonButton
                        fill="clear"
                        size="small"
                        className="feedback-attachments-remove"
                        onClick={() => handleAttachmentRemove(2)}
                      >
                        Remove
                      </IonButton>
                    </div>
                  ) : (
                    <IonButton
                      className="btn-default btn-inverted feedback-attachments-button feedback-attachments-secondary-button"
                      onClick={() => document.getElementById(attachmentInputTwoId)?.click()}
                    >
                      Add another file
                    </IonButton>
                  )}
                </>
              ) : null}

              {!attachmentOne && !attachmentTwo ? (
                <p className="instructions pt-1 feedback-attachments-filenames">
                  {t('feedback.noFilesSelected')}
                </p>
              ) : null}
              <p className="instructions pt-1">
                {t('feedback.attachmentInfo', {
                  count: MAX_ATTACHMENTS,
                  size: 2,
                })}
              </p>
            </div>
          </IonItem>
        </IonList>

        <div className="pt-3 ion-text-center">
          <IonButton
            className="btn-default btn-wide-80"
            disabled={isSubmitting || !customerLoaded || !cardsLoaded}
            onClick={handleSubmit}
          >
            {t('feedback.submit')}
          </IonButton>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default FeedbackPage;
